/**
 * usePhotoUpload — three-step upload flow that mirrors photo-upload-token's
 * contract:
 *   1. POST /photo-upload-token         → signed upload URL + confirm body
 *   2. PUT  <signed_url>                → file bytes
 *   3. POST /photo-upload-token?action=confirm → registers row in room_photos
 *
 * On success, invalidates the task-photos and cleaning-assistant queries so
 * the UI reflects the new upload immediately.
 */

import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query'
import { useSupabase }                                          from './useSupabase'
import { SB_URL }                                               from '@/lib/supabase'
import { AppError }                                             from '@/lib/errors/AppError'
import { isAllowedMimeType, isWithinSizeLimit }                 from '@/lib/storage/photoPath'

interface UploadVars {
  file:       File
  category:   string
  roomId:     string
}

interface UploadResult {
  photoId:  string
  category: string
}

interface UseUploadOpts { taskId: string | undefined }

export function usePhotoUpload(
  { taskId }: UseUploadOpts,
): UseMutationResult<UploadResult, AppError, UploadVars> {
  const supabase     = useSupabase()
  const queryClient  = useQueryClient()

  return useMutation<UploadResult, AppError, UploadVars>({
    mutationFn: async ({ file, category, roomId }): Promise<UploadResult> => {
      if (!taskId) throw makeErr('NO_TASK', 'No task selected', 400)
      if (!isAllowedMimeType(file.type)) {
        throw makeErr('INVALID_TYPE', 'Only JPEG, PNG, and WebP images are accepted.', 415)
      }
      if (!isWithinSizeLimit(file.size)) {
        throw makeErr('FILE_TOO_LARGE', 'Photo must be 15 MB or smaller.', 413)
      }

      // Idempotency keys: distinct per route. The "issue" key + "confirm"
      // key are independent. Both are scoped per-attempt — a retry of the
      // *same* attempt re-uses the same pair, so a duplicated network
      // request short-circuits to the cached response.
      const issueKey   = crypto.randomUUID()
      const confirmKey = crypto.randomUUID()

      // 1. Get signed upload URL from photo-upload-token
      const { data: token, error: tokenErr } = await supabase.functions.invoke<{
        upload_url:   string
        storage_path: string
        confirm_with: Record<string, unknown>
      }>('photo-upload-token', {
        body: {
          room_id:        roomId,
          photo_category: category,
          filename:       file.name,
          content_type:   file.type,
          task_id:        taskId,
        },
        headers: { 'Idempotency-Key': issueKey },
      })
      if (tokenErr || !token) {
        throw makeErr('TOKEN_FAILED', tokenErr?.message ?? 'Could not start upload.', 500)
      }

      // 2. Upload file bytes directly to the signed URL
      const putRes = await fetch(token.upload_url, {
        method:  'PUT',
        headers: { 'Content-Type': file.type },
        body:    file,
      })
      if (!putRes.ok) {
        throw makeErr('UPLOAD_FAILED', `Upload returned ${putRes.status}`, putRes.status)
      }

      // 3. Confirm — registers the room_photos row
      const session = (await supabase.auth.getSession()).data.session
      if (!session) throw makeErr('NO_SESSION', 'Sign in expired. Please sign in again.', 401)

      const sbUrl = SB_URL.replace(/\/$/, '')
      const confirmRes = await fetch(`${sbUrl}/functions/v1/photo-upload-token?action=confirm`, {
        method:  'POST',
        headers: {
          'Authorization':   `Bearer ${session.access_token}`,
          'Content-Type':    'application/json',
          'Idempotency-Key': confirmKey,
        },
        body: JSON.stringify(token.confirm_with),
      })
      const confirmJson = await confirmRes.json().catch(() => ({})) as { photo_id?: string; error?: string; detail?: string }
      if (!confirmRes.ok || !confirmJson.photo_id) {
        throw makeErr(confirmJson.error ?? 'CONFIRM_FAILED', confirmJson.detail ?? 'Could not register the upload.', confirmRes.status)
      }

      return { photoId: confirmJson.photo_id, category }
    },

    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['task-photos',         taskId] })
      void queryClient.invalidateQueries({ queryKey: ['cleaning-assistant',  taskId] })
    },
  })
}

function makeErr(code: string, userMessage: string, status: number): AppError {
  return new AppError({
    code,
    message:     userMessage,
    userMessage,
    status,
  })
}
